import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Card, Divider, Grid, IconButton, Stack, Modal } from "@mui/material";
import SoftBox from "components/SoftBox";
import DatePicker from "react-datepicker";
// Without this the calendar renders as a bare unstyled column of day numbers
// spilling down the page — react-datepicker ships no CSS-in-JS. Every other
// DatePicker page in the app imports it; this one never did.
import "react-datepicker/dist/react-datepicker.css";
import KendoInput from "assets/theme/components/inputKendo";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormField from "pages/components/FormField";
import KendoButton from "assets/theme/components/button/kendo";
import SoftTypography from "components/SoftTypography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SoftButton from "components/SoftButton";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import toast from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { INVOICE_STATUS_LABELS, QUOTATION_STATUS_LABELS } from "config/statusLabels";
import dayjs from "dayjs";
import AddQuotationModal from "../AddQuotationModal";
import AddPartPickerModal from "./AddPartPickerModal";
import AddServicePickerModal from "./AddServicePickerModal";
import AddSubscriptionPickerModal from "./AddSubscriptionPickerModal";
import PropTypes from "prop-types";
import { OrganisationsApiService } from "api/admin.api";
import Select from "react-select";
import Styles from "pages/components/common/Styles";
import { selectUserDetails, SOFTWARE_VENDOR_TYPE } from "config/appMode";
import { QUOTES_LIST_ROUTE } from "pages/Quotes/utils/routes";

const mapQuoteItems = (quote) => {
  const items = Array.isArray(quote?.items) ? quote.items : [];

  return items.map((item, index) => ({
    id: item.id || `${quote?.id || "quote"}-${index}`,
    name: item.name || "",
    itemNo: item.itemNo || String(item.id || index + 1),
    quantity: String(item.quantity || 1),
    price: String(item.price || 0),
    rate: String(item.rate || item.price || 0),
    description: item.description || "",
    templateId: item.templateId || null,
    thumbnail: item.thumbnail || null,
    // PR 3: preserve discriminator + catalog ids when round-tripping an
    // existing quote through the editor so re-saving doesn't drop them.
    lineKind: item.lineKind || "custom",
    partId: item.partId || null,
    serviceId: item.serviceId || null,
    partNumber: item.partNumber || null,
    partLocationName: item.partLocationName || null,
    partAvailableQuantity: item.partAvailableQuantity ?? null,
    serviceUnitOfMeasure: item.serviceUnitOfMeasure || null,
    subscriptionPlanId: item.subscriptionPlanId || null,
    subscriptionTerm: item.subscriptionTerm || null,
    subscriptionLocationId: item.subscriptionLocationId || null,
    subscriptionPlanUuid: item.subscriptionPlanUuid || null,
    subscriptionLocationName: item.subscriptionLocationName || null,
    subscriptionTermMonths: item.subscriptionTermMonths ?? null,
    subscriptionEndDate: item.subscriptionEndDate || null,
  }));
};

// Spells out a flat line's term from whichever half the line carries.
// mapQuoteItems keeps both across an edit round-trip, so this reads the same
// on a freshly-added line and a reloaded one.
const subscriptionTermSuffix = (item) => {
  if (item?.subscriptionTermMonths) {
    const m = item.subscriptionTermMonths;
    return m % 12 === 0
      ? ` · ${m / 12} ${m / 12 === 1 ? "year" : "years"}`
      : ` · ${m} ${m === 1 ? "month" : "months"}`;
  }
  if (item?.subscriptionEndDate)
    return ` · until ${String(item.subscriptionEndDate).slice(0, 10)}`;
  return "";
};

// Standard trade terms. Values are stored verbatim in Quotations.paymentTerms
// (varchar 100) — the column is free text, so this list is a convenience, not
// a constraint, and "Custom…" always falls back to typing your own.
const PAYMENT_TERMS_PRESETS = [
  "Due on Receipt",
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Net 90",
  "2/10 Net 30",
  "1/10 Net 30",
  "50% Deposit, Balance on Completion",
  "Cash on Delivery",
  "Payment in Advance",
  "End of Month",
];

const CUSTOM_TERMS = "__custom__";

const CreateQuotation = () => {
  const [searchParams] = useSearchParams();
  const [dueDate, setDueDate] = useState(new Date());
  const [customerAccountDate, setCustomerAccountDate] = useState(new Date());
  const [loading, setLoading] = React.useState(false);
  const [quoteLoading, setQuoteLoading] = React.useState(false);
  const [data] = React.useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [openQuotationModal, setOpenQuotationModal] = useState(false);
  const [openPartPicker, setOpenPartPicker] = useState(false);
  const [openServicePicker, setOpenServicePicker] = useState(false);
  const [openSubscriptionPicker, setOpenSubscriptionPicker] = useState(false);
  // "" = none picked, a preset string, or CUSTOM_TERMS to reveal the free-text
  // box. Derived from the saved value when an existing quote loads.
  const [paymentTermsPreset, setPaymentTermsPreset] = useState("");
  // Only an org carrying the "Software & Subscriptions" vendor type sells
  // platform plans. Same gate the Fixed Ops sidebar uses for the Software
  // Catalog group; the server re-checks it in QuotationRepository.
  const authUser = useSelector((state) => state.auth.user);
  const canSellSubscriptions = useMemo(
    () =>
      (selectUserDetails(authUser)?.organizationVendorTypes || []).includes(SOFTWARE_VENDOR_TYPE),
    [authUser]
  );
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [owners, setOwners] = useState([]);
  const [prefillOrganisationId, setPrefillOrganisationId] = useState(null);
  const navigate = useNavigate();
  const quoteId = searchParams.get("quoteId");
  const isEditMode = Boolean(quoteId);

  const loadOwners = async () => {
    try {
      const response = await OrganisationsApiService.getAllOrganisations({ pageSize: 1000 });
      const organisations = response?.data?.data?.data ?? response?.data?.data;
      setOwners(Array.isArray(organisations) ? organisations : []);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    loadOwners();
  }, []);

  React.useEffect(() => {
    if (!prefillOrganisationId || owners.length === 0) return;
    const organisation = owners.find((owner) => String(owner.id) === String(prefillOrganisationId));
    setSelectedOwner(organisation || null);
  }, [owners, prefillOrganisationId]);

  const ownerOptions = React.useMemo(() => {
    if (!owners || owners.length === 0) return [];
    return owners?.map((org) => ({
      value: org.id, // Organisation ID
      ownerId: org.ownerId, // Owner/User ID
      label: `${org.name} - ${org.ownerName} (${org.ownerEmail})`,
      ownerEmail: org.ownerEmail,
    }));
  }, [owners]);

  const handleAddItem = () => {
    setOpenQuotationModal(true);
  };

  const handleQuotationItemsAdd = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const itemsWithCustomer = items.map((item) => ({
      ...item,
      lineKind: item.lineKind || "custom",
      selectedOwner: selectedOwner,
    }));
    setOrderItems((prev) => [...prev, ...itemsWithCustomer]);
    setOpenQuotationModal(false);
  };

  const handlePartLineAdd = (item) => {
    setOrderItems((prev) => [...prev, { ...item, selectedOwner }]);
    setOpenPartPicker(false);
  };

  const handleServiceLineAdd = (item) => {
    setOrderItems((prev) => [...prev, { ...item, selectedOwner }]);
    setOpenServicePicker(false);
  };

  const handleSubscriptionLineAdd = (item) => {
    setOrderItems((prev) => [...prev, { ...item, selectedOwner }]);
    setOpenSubscriptionPicker(false);
  };

  const handleInputChange = (index, field, value) => {
    const updatedItems = [...orderItems];
    updatedItems[index][field] = value;
    setOrderItems(updatedItems);
  };

  const handleRemoveItem = (indexToRemove) => {
    const updatedItems = orderItems.filter((_, index) => index !== indexToRemove);
    setOrderItems(updatedItems);
  };

  const initialValues = {
    status: 0,
    name: "",
    quantity: "",
    rate: "",
    price: "",
    dueDate: "",
    note: "",
    soNumber: "",
    poNumber: "",
    paymentTerms: "",
    paymentMode: "",
    trackingNumber: "",
    shipVia: "",
    shippingAccoount: "",
    downPayment: "0",
    paidToDate: "0",
    customerAccountDate: "",
    shippingRemarks: "",
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    // None of the header fields is mandatory to quote someone — an SO number
    // gets assigned later, shipping details aren't known yet, and terms are
    // often agreed after the quote goes out. Only length/format limits and the
    // things the document genuinely can't do without (an organisation, at
    // least one line) are enforced; the first two are checked in onSubmit
    // because they don't live in formik state.
    validationSchema: Yup.object({
      note: Yup.string().max(250, "Remarks must be 250 characters or less"),
      soNumber: Yup.string().max(100, "SO number must be 100 characters or less"),
      poNumber: Yup.string().max(100, "PO number must be 100 characters or less"),
      paymentTerms: Yup.string().max(100, "Payment terms must be 100 characters or less"),
      trackingNumber: Yup.string().max(100, "Tracking number must be 100 characters or less"),
      shipVia: Yup.string().max(100, "Ship via must be 100 characters or less"),
      shippingAccoount: Yup.string().max(
        100,
        "Shipping account number must be 100 characters or less"
      ),
      downPayment: Yup.number().typeError("Down Payment must be a number").optional(),
      paidToDate: Yup.number().typeError("Paid To Date must be a number").optional(),
      shippingRemarks: Yup.string().max(250, "Shipping Remarks must be 250 characters or less"),
    }),
    onSubmit: async (values) => {
      console.log("Submitted Order Data:", values);

      if (!selectedOwner?.id) {
        toast.error("Please select an organisation");
        return;
      }
      if (orderItems.length === 0) {
        toast.error("Add at least one line item");
        return;
      }

      // Validate order items
      if (orderItems.length === 0) {
        toast.error("Please add at least one work order item");
        return;
      }

      for (let i = 0; i < orderItems.length; i++) {
        const item = orderItems[i];

        if (!item.name || !item.itemNo) {
          toast.error(`Please add a valid quote item for row ${i + 1}`);
          return;
        }

        const quantity = Number(item.quantity);
        if (!item.quantity || Number.isNaN(quantity) || quantity <= 0) {
          toast.error(`Please enter a valid quantity (greater than 0) for item ${i + 1}`);
          return;
        }

        const price = Number(item.price);
        if (!item.price || Number.isNaN(price) || price <= 0) {
          toast.error(`Please enter a valid price (greater than 0) for item ${i + 1}`);
          return;
        }
      }

      const totalPrice = getTotalPrice();
      if (totalPrice <= 0) {
        toast.error("Total price must be greater than zero");
        return;
      }

      const balanceDue = getBalanceDue();

      const formData = {
        amount: Number(getTotalPrice().toFixed(2)),
        status: Number(values.status) || 0,
        dueDate: values.dueDate,
        note: values.note,
        userProfileId: selectedOwner?.ownerId || null,
        organisationId: selectedOwner?.id || null,
        balanceDue: Number(balanceDue),
        paidToDate: Number(values.paidToDate) || 0,
        downPayment: Number(values.downPayment) || 0,
        soNumber: values.soNumber || "",
        poNumber: values.poNumber || "",
        paymentTerms: values.paymentTerms || "",
        paymentMode: values.paymentMode || "",
        trackingNumber: values.trackingNumber || "",
        shipVia: values.shipVia || "",
        shippingAccoount: values.shippingAccoount || "",
        shippingRemarks: values.shippingRemarks || "",
        customerAccountDate: values.customerAccountDate || null,
        workOrderId: values.workOrderId || null,
        items: orderItems.map((item) => ({
          name: item.name || "",
          workOrderId: item.selectedItem?.id || null,
          quantity: Number(item.quantity) || 0,
          rate: Number(item.rate || item.price) || 0,
          price: Number(item.price) || 0,
          description: item.description || "",
          itemNo: item.itemNo || "",
          productId: item.selectedProduct?.productId || item.selectedProduct?.id || null,
          templateId: item.templateId || null,
          lineKind: item.lineKind || "custom",
          partId: item.lineKind === "part" ? item.partId || null : null,
          serviceId: item.lineKind === "service" ? item.serviceId || null : null,
          subscriptionPlanId:
            item.lineKind === "subscription" ? item.subscriptionPlanId || null : null,
          subscriptionTerm:
            item.lineKind === "subscription" ? item.subscriptionTerm || null : null,
          subscriptionLocationId:
            item.lineKind === "subscription" ? item.subscriptionLocationId || null : null,
          subscriptionTermMonths:
            item.lineKind === "subscription" ? item.subscriptionTermMonths ?? null : null,
          subscriptionEndDate:
            item.lineKind === "subscription" ? item.subscriptionEndDate || null : null,
        })),
      };

      await saveData(formData);
      // Add your submission logic here
    },
  });

  // Was `status >= 0`, which is true of every status (0 Draft … 5 Cancelled) —
  // so opening a quote to edit it produced a form that couldn't be changed.
  // A quote stays editable until the customer has acted on it: 2 = Accepted,
  // 3 = Paid. The server applies the same rule to delete.
  const isReadOnly = isEditMode && [2, 3].includes(Number(formik.values.status));

  console.log("values", formik.values);
  console.log("errors", formik.errors);

  React.useEffect(() => {
    if (dueDate) {
      formik.setFieldValue("dueDate", dayjs(dueDate).format("YYYY-MM-DD"));
    }
  }, [dueDate]);

  React.useEffect(() => {
    if (customerAccountDate) {
      formik.setFieldValue("customerAccountDate", dayjs(customerAccountDate).format("YYYY-MM-DD"));
    }
  }, [customerAccountDate]);

  React.useEffect(() => {
    const loadQuotation = async () => {
      try {
        setQuoteLoading(true);
        const response = await AdminQuotationApiService.getQuotation(quoteId);
        const quote = response?.data?.data;

        if (!quote) {
          return;
        }

        setPrefillOrganisationId(
          quote.customerOrganisationId || quote.billingAddress?.organisationId || null
        );
        setOrderItems(mapQuoteItems(quote));

        if (quote.dueDate) {
          setDueDate(new Date(quote.dueDate));
        }

        const savedTerms = quote.paymentTerms || "";
        setPaymentTermsPreset(
          savedTerms && !PAYMENT_TERMS_PRESETS.includes(savedTerms) ? CUSTOM_TERMS : savedTerms
        );

        if (quote.customerAccountDate) {
          setCustomerAccountDate(new Date(quote.customerAccountDate));
        }

        formik.setValues({
          status: quote.status ?? 2,
          name: "",
          quantity: "",
          rate: "",
          price: String(Number(quote.amount || 0)),
          dueDate: quote.dueDate ? dayjs(quote.dueDate).format("YYYY-MM-DD") : "",
          note: quote.note || "",
          soNumber: quote.soNumber || "",
          poNumber: quote.customerPoNumber || "",
          paymentTerms: quote.paymentTerms || "",
          paymentMode: quote.paymentMode || "",
          trackingNumber: quote.trackingNumber || "",
          shipVia: quote.shipVia || "",
          shippingAccoount: quote.shippingAccNumber || "",
          downPayment: String(Number(quote.downPayment || 0)),
          paidToDate: String(Number(quote.paidToDate || 0)),
          customerAccountDate: quote.customerAccountDate
            ? dayjs(quote.customerAccountDate).format("YYYY-MM-DD")
            : "",
          shippingRemarks: quote.shippingRemarks || "",
        });
      } catch (error) {
        console.error(error);
        toast.error("Failed to load quotation");
      } finally {
        setQuoteLoading(false);
      }
    };

    if (quoteId) {
      loadQuotation();
    }
  }, [quoteId]);

  // Calculate price based on quantity (seconds) and rate (hourly rate)
  useEffect(() => {
    const qty = Number(formik.values.quantity);
    const labourRate = Number(formik.values.rate);
    if (!Number.isNaN(qty) && !Number.isNaN(labourRate)) {
      const calculatedPrice = (qty / 3600) * labourRate; // convert seconds to hours
      formik.setFieldValue("price", calculatedPrice.toFixed(2));
    } else {
      formik.setFieldValue("price", "");
    }
  }, [formik.values.quantity, formik.values.rate]);

  // Calculate total price from order items
  useEffect(() => {
    if (orderItems.length > 0) {
      const total = orderItems.reduce((sum, item) => {
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        return sum + quantity * price;
      }, 0);
      formik.setFieldValue("price", total.toFixed(2));
    }
  }, [orderItems]);

  // Validate downPayment and paidToDate not exceeding price
  useEffect(() => {
    const price = getTotalPrice();
    const downPayment = Number(formik.values.downPayment) || 0;
    const paidToDate = Number(formik.values.paidToDate) || 0;

    if (price <= 0) return;

    if (downPayment > price) {
      formik.setFieldValue("downPayment", price.toFixed(2));
    }
    if (paidToDate > price) {
      formik.setFieldValue("paidToDate", price.toFixed(2));
    }
  }, [formik.values.price, formik.values.downPayment, formik.values.paidToDate, orderItems]);

  const getDownPayment = () => Number(formik.values.downPayment) || 0;
  const getPaidToDate = () => Number(formik.values.paidToDate) || 0;
  const getTotalPrice = () => {
    if (orderItems.length > 0) {
      return orderItems.reduce((sum, item) => {
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        return sum + quantity * price;
      }, 0);
    }
    return Number(formik.values.price) || 0;
  };

  const getBalanceDue = () => {
    const price = getTotalPrice();
    const paidTotal = getDownPayment() + getPaidToDate();
    return Math.max(price - paidTotal, 0).toFixed(2);
  };

  const saveData = async (formData) => {
    try {
      setLoading(true);
      const response = isEditMode
        ? await AdminQuotationApiService.updateQuotation(quoteId, formData)
        : await AdminQuotationApiService.createQuotation(formData);
      if (response.data.success) {
        toast.success(
          isEditMode ? "Quotation updated successfully" : "Quotation created successfully"
        );
        setTimeout(() => {
          navigate(QUOTES_LIST_ROUTE);
        }, 500);
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error(error?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {/* No DashboardNavbar — portal shell already supplies the header.
          Full-width: outer SoftBox provides the 20px gutter to the
          sidebar; Grid spans lg={12} so the Card stretches edge to edge.
      */}
      <SoftBox py={3} px={2.5}>
        <Link to={""}>
          <SoftTypography variant="h5" fontSize="medium" onClick={() => navigate(-1)}>
            Go Back
          </SoftTypography>
        </Link>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <Card sx={{ padding: 4, backgroundColor: "#fcffff" }}>
            <SoftBox component="form" role="form" onSubmit={formik.handleSubmit}>
              <Grid container spacing={1}>
                <Grid item xs={12} mb={1.5}>
                  <SoftTypography variant="h3">
                    {isEditMode ? "Edit Quotation Details" : "Enter Quotation Details"}
                  </SoftTypography>
                  {quoteLoading && (
                    <SoftTypography variant="button" color="text">
                      Loading Quotation...
                    </SoftTypography>
                  )}
                </Grid>
                <Grid item xs={12} mt={1} mb={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Organisation
                  </SoftTypography>
                  <Select
                    placeholder="Select Organisation"
                    isClearable
                    isSearchable
                    isDisabled={isReadOnly}
                    options={ownerOptions}
                    value={
                      selectedOwner
                        ? ownerOptions.find((opt) => opt.value === selectedOwner.id)
                        : null
                    }
                    onChange={(selectedOption) => {
                      const org = owners?.find((o) => o.id === selectedOption?.value);
                      setSelectedOwner(org || null);
                    }}
                    styles={Styles.multiSelectStyles}
                  />
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    So No.
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter so number"
                    name="soNumber"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.soNumber}
                    disabled={isReadOnly}
                  />
                  {formik.touched.soNumber && formik.errors.soNumber && (
                    <div className="form-error error">{formik.errors.soNumber}</div>
                  )}
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Due Date
                  </SoftTypography>
                  <DatePicker
                    selected={dueDate}
                    onChange={(date) => setDueDate(date)}
                    dateFormat="MMMM d, yyyy"
                    placeholderText="Pick a due date"
                    className="custom-date-picker"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    autoComplete="off"
                    isClearable={!isReadOnly}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Customer PO No.
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter customer po number"
                    name="poNumber"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.poNumber}
                    disabled={isReadOnly}
                  />
                  {formik.touched.poNumber && formik.errors.poNumber && (
                    <div className="form-error error">{formik.errors.poNumber}</div>
                  )}
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Payment Terms
                  </SoftTypography>
                  <SoftBox className="c-select-wrapper">
                    <select
                      className="c-select"
                      name="paymentTermsPreset"
                      value={paymentTermsPreset}
                      onChange={(e) => {
                        const picked = e.target.value;
                        setPaymentTermsPreset(picked);
                        // A preset writes straight through to the stored value;
                        // "Custom…" clears it so the box below starts empty.
                        formik.setFieldValue(
                          "paymentTerms",
                          picked === CUSTOM_TERMS ? "" : picked
                        );
                      }}
                      disabled={isReadOnly}
                    >
                      <option value="">No terms specified</option>
                      {PAYMENT_TERMS_PRESETS.map((t) => (
                        <option value={t} key={t}>
                          {t}
                        </option>
                      ))}
                      <option value={CUSTOM_TERMS}>Custom…</option>
                    </select>
                  </SoftBox>
                  {paymentTermsPreset === CUSTOM_TERMS && (
                    <SoftBox mt={1}>
                      <KendoInput
                        placeholder="e.g. Net 21, 3% discount if paid in 7 days"
                        name="paymentTerms"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.paymentTerms}
                        disabled={isReadOnly}
                      />
                    </SoftBox>
                  )}
                  {formik.touched.paymentTerms && formik.errors.paymentTerms && (
                    <div className="form-error error">{formik.errors.paymentTerms}</div>
                  )}
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Payment Mode
                  </SoftTypography>

                  <SoftBox className="c-select-wrapper">
                    <select
                      className="c-select"
                      name="paymentMode"
                      value={formik.values.paymentMode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isReadOnly}
                    >
                      <option value={""}>Not specified</option>
                      <option value={"Credit Card"}>Credit Card</option>
                      <option value={"Debit Card"}>Debit Card</option>
                      <option value={"PO / Check"}>PO / Check</option>
                    </select>
                  </SoftBox>

                  {formik.touched.paymentMode && formik.errors.paymentMode ? (
                    <div className="form-error error">{formik.errors.paymentMode}</div>
                  ) : null}
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Tracking No.
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter tracking number"
                    name="trackingNumber"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.trackingNumber}
                    disabled={isReadOnly}
                  />
                  {formik.touched.trackingNumber && formik.errors.trackingNumber && (
                    <div className="form-error error">{formik.errors.trackingNumber}</div>
                  )}
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Ship Via
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter ship via"
                    name="shipVia"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.shipVia}
                    disabled={isReadOnly}
                  />
                  {formik.touched.shipVia && formik.errors.shipVia && (
                    <div className="form-error error">{formik.errors.shipVia}</div>
                  )}
                </Grid>
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Shipping account number
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter shipping account number"
                    name="shippingAccoount"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.shippingAccoount}
                    disabled={isReadOnly}
                  />
                  {formik.touched.shippingAccoount && formik.errors.shippingAccoount && (
                    <div className="form-error error">{formik.errors.shippingAccoount}</div>
                  )}
                </Grid>

                {/* Down Payment */}
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Down Payment
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter down payment"
                    name="downPayment"
                    type="number"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.downPayment}
                    disabled={isReadOnly}
                  />
                  {formik.touched.downPayment && formik.errors.downPayment && (
                    <div className="form-error error">{formik.errors.downPayment}</div>
                  )}
                </Grid>

                {/* Paid To Date */}
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Paid To Date
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter paid to date"
                    name="paidToDate"
                    type="number"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.paidToDate}
                    disabled={isReadOnly}
                  />
                  {formik.touched.paidToDate && formik.errors.paidToDate && (
                    <div className="form-error error">{formik.errors.paidToDate}</div>
                  )}
                </Grid>

                {/* Customer Account as Of */}
                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Customer Account as Of
                  </SoftTypography>
                  <DatePicker
                    selected={customerAccountDate}
                    onChange={(date) => setCustomerAccountDate(date)}
                    dateFormat="MMMM d, yyyy"
                    placeholderText="Pick a date"
                    className="custom-date-picker"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    autoComplete="off"
                    isClearable={!isReadOnly}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item md={6} xs={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Status
                  </SoftTypography>
                  <SoftBox className="c-select-wrapper">
                    <select
                      className="c-select"
                      name="status"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isReadOnly}
                    >
                      {QUOTATION_STATUS_LABELS.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </SoftBox>

                  {formik.touched.status && formik.errors.status ? (
                    <div className="form-error error">{formik.errors.status}</div>
                  ) : null}
                </Grid>

                {/* Order Items Component */}
                <OrderItems
                  orderItems={orderItems}
                  handleAddItem={handleAddItem}
                  handleAddPart={() => setOpenPartPicker(true)}
                  handleAddService={() => setOpenServicePicker(true)}
                  handleAddSubscription={() => setOpenSubscriptionPicker(true)}
                  canSellSubscriptions={canSellSubscriptions}
                  customerOrganisationId={selectedOwner?.id ?? null}
                  handleInputChange={handleInputChange}
                  handleRemoveItem={handleRemoveItem}
                  formik={formik}
                  openModal={openQuotationModal}
                  onAddItems={handleQuotationItemsAdd}
                  handleCloseModal={() => setOpenQuotationModal(false)}
                  openPartPicker={openPartPicker}
                  closePartPicker={() => setOpenPartPicker(false)}
                  onAddPart={handlePartLineAdd}
                  openServicePicker={openServicePicker}
                  closeServicePicker={() => setOpenServicePicker(false)}
                  onAddService={handleServiceLineAdd}
                  openSubscriptionPicker={openSubscriptionPicker}
                  closeSubscriptionPicker={() => setOpenSubscriptionPicker(false)}
                  onAddSubscription={handleSubscriptionLineAdd}
                  isReadOnly={isReadOnly}
                />

                {/* Price Summary */}
                <Grid item xs={12} mt={3}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={4}>
                    <SoftTypography fontWeight="bold" letterSpacing={0.3} variant="h6">
                      Price:
                    </SoftTypography>
                    <SoftTypography fontWeight="bold" variant="h6">
                      ${getTotalPrice().toFixed(2)}
                    </SoftTypography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={4}>
                    <SoftTypography fontWeight="bold" letterSpacing={0.3} variant="h6">
                      Tax:
                    </SoftTypography>
                    <SoftTypography fontWeight="bold" variant="h6">
                      $0.00
                    </SoftTypography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={4}>
                    <SoftTypography fontWeight="bold" letterSpacing={0.3} variant="h6">
                      Down payment:
                    </SoftTypography>
                    <SoftTypography fontWeight="bold" variant="h6">
                      ${getDownPayment().toFixed(2)}
                    </SoftTypography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={4}>
                    <SoftTypography fontWeight="bold" letterSpacing={0.3} variant="h6">
                      Doc Total:
                    </SoftTypography>
                    <SoftTypography fontWeight="bold" variant="h6">
                      ${getTotalPrice().toFixed(2)}
                    </SoftTypography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={4}>
                    <SoftTypography fontWeight="bold" letterSpacing={0.3} variant="h6">
                      Paid To date:
                    </SoftTypography>
                    <SoftTypography fontWeight="bold" variant="h6">
                      ${getPaidToDate().toFixed(2)}
                    </SoftTypography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={4}>
                    <SoftTypography fontWeight="bold" letterSpacing={0.3} variant="h5">
                      Balance Due:
                    </SoftTypography>
                    <SoftTypography fontWeight="bold">${getBalanceDue()}</SoftTypography>
                  </Box>
                </Grid>
              </Grid>
              <Divider />
              <Grid item xs={12}>
                <FormField
                  placeholder="Type your message..."
                  name="note"
                  label="Remarks"
                  mt={5}
                  multiline
                  rows={4}
                  maxLength="250"
                  value={formik.values.note}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isReadOnly}
                />
                {formik.touched.note && formik.errors.note && (
                  <div className="form-error error">{formik.errors.note}</div>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormField
                  placeholder="Type your message..."
                  name="shippingRemarks"
                  label="Shipping Remarks"
                  mt={5}
                  multiline
                  rows={3}
                  maxLength="250"
                  value={formik.values.shippingRemarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isReadOnly}
                />
                {formik.touched.shippingRemarks && formik.errors.shippingRemarks && (
                  <div className="form-error error">{formik.errors.shippingRemarks}</div>
                )}
              </Grid>

              <SoftBox display="flex" justifyContent="flex-end" alignItems="center" mt={3} gap={1}>
                {!isReadOnly && (
                  <KendoButton
                    type="submit"
                    label={isEditMode ? "Update Quote" : "Create Quote"}
                    loading={loading}
                  />
                )}
              </SoftBox>
            </SoftBox>
          </Card>
        </Grid>
      </Grid>
      </SoftBox>
    </DashboardLayout>
  );
};

const OrderItems = ({
  orderItems,
  handleAddItem,
  handleAddPart,
  handleAddService,
  handleAddSubscription,
  canSellSubscriptions,
  customerOrganisationId,
  handleInputChange,
  handleRemoveItem,
  formik,
  openModal,
  onAddItems,
  handleCloseModal,
  openPartPicker,
  closePartPicker,
  onAddPart,
  openServicePicker,
  closeServicePicker,
  onAddService,
  openSubscriptionPicker,
  closeSubscriptionPicker,
  onAddSubscription,
  isReadOnly = false,
}) => {
  const [expandedImage, setExpandedImage] = useState(null);

  const handleImageClick = (imageUrl) => {
    setExpandedImage(imageUrl);
  };

  const handleCloseImage = () => {
    setExpandedImage(null);
  };

  return (
    <>
      <Grid container alignItems="end" justifyContent="space-between" mt={3} mb={2} ml={1}>
        <Grid item>
          <SoftTypography variant="h5" mb={0}>
            Quotation Items
          </SoftTypography>
        </Grid>
        {!isReadOnly && (
          <Grid item>
            <Stack direction="row" spacing={1}>
              <SoftButton variant="outlined" color="info" size="small" onClick={handleAddItem}>
                + Add Item
              </SoftButton>
              <SoftButton variant="outlined" color="dark" size="small" onClick={handleAddPart}>
                + Add Part
              </SoftButton>
              <SoftButton variant="outlined" color="primary" size="small" onClick={handleAddService}>
                + Add Service
              </SoftButton>
              {canSellSubscriptions && (
                <SoftButton
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={handleAddSubscription}
                >
                  + Add Subscription
                </SoftButton>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>

      {/* Table-like Header */}
      {orderItems.length > 0 && (
        <Grid container spacing={1} ml={0.3} mb={1}>
          <Grid item lg={6} md={5} xs={12}>
            <SoftTypography variant="caption" fontWeight="bold">
              Item
            </SoftTypography>
          </Grid>
          <Grid item lg={2} md={2} xs={4}>
            <SoftTypography variant="caption" fontWeight="bold">
              Quantity
            </SoftTypography>
          </Grid>
          <Grid item lg={2} md={2} xs={4}>
            <SoftTypography variant="caption" fontWeight="bold">
              Amount
            </SoftTypography>
          </Grid>
          <Grid item lg={2} md={2} xs={4}>
            <SoftTypography variant="caption" fontWeight="bold">
              Price
            </SoftTypography>
          </Grid>
        </Grid>
      )}

      {orderItems.map((item, index) => {
        const lineTotal = (Number(item.quantity) || 0) * (Number(item.price) || 0);
        return (
          <Grid
            container
            alignItems="center"
            spacing={1}
            key={`${item.itemNo || "item"}-${item.selectedItem?.id || "na"}-${index}`}
            flexWrap="wrap"
            ml={0.3}
            mb={1.5}
          >
            <Grid item lg={6} md={5} xs={12}>
              <SoftBox
                width="100%"
                sx={{
                  border: "1px solid #e0e0e0",
                  padding: 1,
                  px: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minHeight: "48px",
                }}
              >
                {item.thumbnail && (
                  <Box
                    component="img"
                    src={item.thumbnail}
                    sx={{
                      width: "36px",
                      height: "36px",
                      objectFit: "cover",
                      borderRadius: 1,
                      cursor: "pointer",
                      border: "1px solid #eee",
                    }}
                    onClick={() => handleImageClick(item.thumbnail)}
                  />
                )}
                {!item.thumbnail && (
                  <Box
                    sx={{
                      width: "36px",
                      height: "36px",
                      bgcolor: "#f0f2f5",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SoftTypography variant="caption" fontSize="8px">
                      No Img
                    </SoftTypography>
                  </Box>
                )}
                <SoftBox flex={1} sx={{ minWidth: 0 }}>
                  <SoftBox display="flex" alignItems="center" gap={0.75}>
                    <SoftTypography
                      variant="body2"
                      fontSize="12px"
                      color="text"
                      sx={{
                        fontWeight: "medium",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name || ""}
                    </SoftTypography>
                    {item.lineKind === "part" && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.5,
                          backgroundColor: "#E8F0FE",
                          color: "#1A56C0",
                        }}
                      >
                        Part{item.partNumber ? ` · ${item.partNumber}` : ""}
                      </Box>
                    )}
                    {item.lineKind === "service" && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.5,
                          backgroundColor: "#FEF7E0",
                          color: "#80570F",
                        }}
                      >
                        Labor{item.serviceUnitOfMeasure ? ` · ${item.serviceUnitOfMeasure}` : ""}
                      </Box>
                    )}
                    {item.lineKind === "subscription" && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.5,
                          backgroundColor: "#E6F4EA",
                          color: "#14663B",
                        }}
                      >
                        Subscription
                        {item.subscriptionPlanUuid ? ` · ${item.subscriptionPlanUuid}` : ""}
                        {item.subscriptionTerm ? ` · ${item.subscriptionTerm}` : ""}
                        {item.subscriptionTerm === "flat" && subscriptionTermSuffix(item)}
                      </Box>
                    )}
                  </SoftBox>
                  {item.lineKind === "part" && item.partLocationName && (
                    <SoftTypography variant="caption" color="text" fontSize="10px">
                      {item.partAvailableQuantity ?? 0} on hand @ {item.partLocationName}
                    </SoftTypography>
                  )}
                  {item.lineKind === "subscription" && item.subscriptionLocationName && (
                    <SoftTypography variant="caption" color="text" fontSize="10px">
                      Site: {item.subscriptionLocationName}
                    </SoftTypography>
                  )}
                </SoftBox>
              </SoftBox>
            </Grid>

            <Grid item lg={2} md={2} xs={4}>
              <KendoInput
                placeholder="Qty"
                name={`items[${index}].quantity`}
                type="number"
                onChange={(e) => handleInputChange(index, "quantity", e.target.value)}
                onBlur={formik.handleBlur}
                value={item.quantity}
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item lg={2} md={2} xs={4}>
              <KendoInput
                placeholder="Amount"
                name={`items[${index}].price`}
                type="number"
                onChange={(e) => handleInputChange(index, "price", e.target.value)}
                onBlur={formik.handleBlur}
                value={item.price}
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item lg={2} md={3} xs={4}>
              <Grid display="flex" alignItems="center" gap={1}>
                <SoftBox
                  sx={{
                    flexGrow: 1,
                    bgcolor: "#f8f9fa",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    minWidth: "60px",
                    textAlign: "right",
                  }}
                >
                  <SoftTypography variant="button" fontWeight="bold">
                    ${lineTotal.toFixed(2)}
                  </SoftTypography>
                </SoftBox>
                {!isReadOnly && (
                  <IconButton
                    variant="gradient"
                    color="error"
                    onClick={() => handleRemoveItem(index)}
                    sx={{ padding: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Grid>
        );
      })}

      {/* Expanded Image Modal */}
      <Modal open={Boolean(expandedImage)} onClose={handleCloseImage}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 1,
            borderRadius: 2,
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
          }}
        >
          <img
            src={expandedImage}
            alt="Expanded item"
            style={{ width: "100%", height: "auto", borderRadius: "4px" }}
          />
        </Box>
      </Modal>

      {/* Work Order + Product Configurator Modal */}
      <AddQuotationModal open={openModal} handleClose={handleCloseModal} onAddItems={onAddItems} />

      {/* PR 3 — Inventory & Services pickers */}
      <AddPartPickerModal
        open={openPartPicker}
        handleClose={closePartPicker}
        onAddItem={onAddPart}
      />
      <AddServicePickerModal
        open={openServicePicker}
        handleClose={closeServicePicker}
        onAddItem={onAddService}
      />
      <AddSubscriptionPickerModal
        open={openSubscriptionPicker}
        handleClose={closeSubscriptionPicker}
        onAddItem={onAddSubscription}
        customerOrganisationId={customerOrganisationId}
      />
    </>
  );
};

OrderItems.propTypes = {
  orderItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleAddItem: PropTypes.func.isRequired,
  handleAddPart: PropTypes.func.isRequired,
  handleAddService: PropTypes.func.isRequired,
  handleAddSubscription: PropTypes.func.isRequired,
  canSellSubscriptions: PropTypes.bool,
  customerOrganisationId: PropTypes.number,
  handleInputChange: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
  formik: PropTypes.shape({
    handleBlur: PropTypes.func,
  }).isRequired,
  openModal: PropTypes.bool.isRequired,
  onAddItems: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  openPartPicker: PropTypes.bool.isRequired,
  closePartPicker: PropTypes.func.isRequired,
  onAddPart: PropTypes.func.isRequired,
  openServicePicker: PropTypes.bool.isRequired,
  closeServicePicker: PropTypes.func.isRequired,
  onAddService: PropTypes.func.isRequired,
  openSubscriptionPicker: PropTypes.bool.isRequired,
  closeSubscriptionPicker: PropTypes.func.isRequired,
  onAddSubscription: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
};

OrderItems.defaultProps = {
  canSellSubscriptions: false,
  customerOrganisationId: null,
};

export default CreateQuotation;
